import React from 'react';
import { BRAND } from '../../lib/brand';
import SourcesEditor, { ExcludeEditor } from './SourcesEditor';
import TransitPanel from './TransitPanel';
import {
  DOMAINS, buildCrawlConfig, excludeEntries, excludeRows, parseTerms, sourceFromProbe, sourceProblems, sourcesFromCrawl,
  staffPayload, staffRows, termsText,
} from './onboarding';
import { ErrorList, Field, apiErrors, btnPrimary, btnSmall, card, hint, inputStyle, warnBox } from './ui';

// Everything about one tenant an operator changes after onboarding, in one
// panel: which sites feed its corpus and how (stored crawl config), the
// assistant basics, the websites allowed to embed it, the domain, its local
// terms, its staff answers, and (transit agencies, or any tenant with trip
// planning on) the GTFS feed.
// Reads GET /api/admin/ops/tenant/<id>; writes through the existing
// endpoints (/api/corpus/crawl, /api/admin/sites/<id>, /api/corpus/domain,
// /api/corpus/local-terms, /api/corpus/staff-answers).

/** An older single-site stored config (frontBase form) as one source row. */
function convertSingleSite(crawl) {
  const extra = {};
  if (crawl.contentBase && crawl.contentBase !== crawl.frontBase) extra.contentBase = crawl.contentBase;
  if (crawl.maxPages) extra.maxPages = crawl.maxPages;
  const site = crawl.frontBase;
  let own = [];
  try {
    const host = new URL(site).hostname;
    const bare = host.replace(/^www\./, '');
    own = [host, bare, `www.${bare}`];
  } catch { /* invalid stored frontBase: validation on save reports it */ }
  const hosts = (crawl.allowedHosts || []).filter((h) => !own.includes(h));
  if (hosts.length) extra.allowedHosts = hosts;
  return [{
    key: site, include: true, site, mode: crawl.sitemapOnly ? 'sitemap' : 'wp',
    sitemapUrl: crawl.sitemapUrl || '', linkedPdfs: !!crawl.linkedPdfs, excludes: excludeRows(crawl.exclude),
    extra, probe: null,
  }];
}

/** The stored config's top-level settings, kept apart from the site rows.
 *  A single-site config's exclude belongs to its one row after conversion. */
function topLevelOf(crawl) {
  if (!crawl) return { sectionRecords: false, notes: '', excludes: [] };
  return {
    sectionRecords: crawl.sectionRecords === true,
    notes: crawl.notes || '',
    excludes: crawl.sources ? excludeRows(crawl.exclude) : [],
  };
}

function SitesTab({ api, detail, onStored, onRecrawl, note }) {
  const stored = detail.crawl;
  const singleSite = stored && !stored.sources;
  const [rows, setRows] = React.useState(() => sourcesFromCrawl(stored));
  const [top, setTop] = React.useState(() => topLevelOf(stored));
  const [json, setJson] = React.useState(() => (singleSite ? JSON.stringify(stored, null, 2) : ''));
  const [asJson, setAsJson] = React.useState(singleSite);
  const [addUrl, setAddUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const probeInto = async (url) => {
    setBusy(true);
    setError(null);
    try {
      const { ok, data } = await api('/api/admin/ops/probe', { method: 'POST', body: JSON.stringify({ urls: [url] }) });
      if (!ok) throw new Error(apiErrors(data, 'probe failed'));
      setRows((prev) => [...prev, sourceFromProbe(data.results[0])]);
      setAddUrl('');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async (crawl, { recrawl = false } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const { ok, data } = await api('/api/corpus/crawl', {
        method: 'PUT', body: JSON.stringify({ corpus_id: detail.channel_url, crawl }),
      });
      if (!ok) {
        setError(apiErrors(data, 'save failed'));
        setBusy(false);
        return;
      }
      if (recrawl && onRecrawl) onRecrawl(detail.channel_url);
      // Reload the panel from what the server stored (normalized, or cleared).
      onStored(data.crawl || null, data.crawl
        ? `Saved${recrawl ? '; recrawl requested' : ''}. The next crawl uses it.`
        : 'Cleared. The crawler falls back to its agency file or derives the config from the URL.');
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const submit = (opts) => {
    if (asJson) {
      let crawl;
      try {
        crawl = JSON.parse(json);
      } catch (e) {
        setError(`Not valid JSON: ${e.message}`);
        return;
      }
      save(crawl, opts);
      return;
    }
    save(buildCrawlConfig(rows, { sectionRecords: top.sectionRecords, notes: top.notes, exclude: excludeEntries(top.excludes) }), opts);
  };
  const problems = asJson ? [] : sourceProblems(rows);

  return (
    <div>
      {!stored && (
        <div style={warnBox}>
          No crawl config is stored for this corpus. Older tenants (CAPA, SFERS, SDCERS, TARC, Mooresville) are crawled from an
          agency file in the crawler repo: move it here first with <code>push-agency-configs.mjs</code>. Saving here replaces
          the agency file with what you build below.
        </div>
      )}
      {asJson ? (
        <>
          <p style={hint}>
            This corpus uses the older single-site config form. Edit it as JSON, or convert it to the sites list.
          </p>
          <textarea aria-label="Crawl config JSON" rows={14} value={json} onChange={(e) => setJson(e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.78rem' }} />
          <button type="button" style={btnSmall} onClick={() => {
            try {
              const parsed = JSON.parse(json);
              setRows(convertSingleSite(parsed));
              setTop(topLevelOf(parsed));
              setAsJson(false);
              setError(null);
            } catch (e) {
              setError(`Not valid JSON: ${e.message}`);
            }
          }}>Convert to sites list</button>
        </>
      ) : (
        <>
          <SourcesEditor rows={rows} onChange={setRows} allowSiteEdit={false} />
          {!rows.length && (
            <button type="button" style={btnSmall} disabled={busy} onClick={() => probeInto(detail.channel_url)}>
              Start from the main site ({detail.channel_url})
            </button>
          )}
          <div style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
            <input aria-label="Add a site URL" value={addUrl} placeholder="Add a site: https://…"
                   style={{ ...inputStyle, marginBottom: 0 }} onChange={(e) => setAddUrl(e.target.value)} />
            <button type="button" style={btnSmall} disabled={busy || !addUrl.trim()} onClick={() => probeInto(addUrl.trim())}>
              {busy ? 'Looking…' : 'Add'}
            </button>
          </div>
          <ExcludeEditor idPrefix="every site" title="Keep out of every site" rows={top.excludes}
                         onChange={(excludes) => setTop({ ...top, excludes })} />
          <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '0.82rem', margin: '10px 0 4px' }}>
            <input type="checkbox" checked={top.sectionRecords}
                   onChange={(e) => setTop({ ...top, sectionRecords: e.target.checked })} />
            Split pages at their headings (section records)
          </label>
          <p style={hint}>Changing this re-chunks and re-embeds every page on the next crawl.</p>
        </>
      )}
      <ErrorList error={error || (problems.length ? problems : null)} />
      {note && <p role="status" style={{ ...hint, color: '#065f46' }}>{note}</p>}
      <div style={{ marginTop: '8px' }}>
        <button type="button" style={btnPrimary} disabled={busy || problems.length > 0} onClick={() => submit()}>Save</button>{' '}
        <button type="button" style={btnSmall} disabled={busy || problems.length > 0} onClick={() => submit({ recrawl: true })}>
          Save and recrawl
        </button>
        {stored && (
          <button type="button" style={{ ...btnSmall, color: BRAND.danger }} disabled={busy}
                  onClick={() => {
                    if (window.confirm('Clear the stored crawl config? The crawler falls back to an agency file or the main URL alone; '
                      + 'other sites stop being refreshed (their pages stay indexed).')) save(null);
                  }}>
            Clear stored config
          </button>
        )}
      </div>
    </div>
  );
}

function AssistantTab({ api, detail, onSaved }) {
  const w = detail.widget || {};
  const p = detail.persona || {};
  const [bot, setBot] = React.useState({
    display_name: w.display_name || '',
    starter_questions: [0, 1, 2, 3].map((i) => (w.starter_questions || [])[i] || ''),
    contact_text: p.contact_text || '',
    refusal_message: p.refusal_message || '',
  });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saved, setSaved] = React.useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    // The PUT replaces the whole widget and persona objects: send everything
    // stored, with this tab's fields changed.
    const widget = { ...w, display_name: bot.display_name.trim(),
                     starter_questions: bot.starter_questions.map((q) => q.trim()).filter(Boolean) };
    const persona = { ...p, contact_text: bot.contact_text.trim(), refusal_message: bot.refusal_message.trim() };
    try {
      const { ok, data } = await api(`/api/admin/sites/${encodeURIComponent(detail.site_id)}`, {
        method: 'PUT', body: JSON.stringify({ status: detail.status || 'active', widget, persona }),
      });
      if (!ok) setError(apiErrors(data, 'save failed'));
      else {
        setSaved(true);
        if (onSaved) onSaved();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (patch) => setBot((b) => ({ ...b, ...patch }));
  return (
    <div>
      <Field id={`${detail.site_id}-display-name`} label="Assistant name">
        <input id={`${detail.site_id}-display-name`} value={bot.display_name} style={inputStyle}
               onChange={(e) => set({ display_name: e.target.value })} />
      </Field>
      <Field id={`${detail.site_id}-starter-1`} label="Starter questions">
        {bot.starter_questions.map((q, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <input key={i} id={`${detail.site_id}-starter-${i + 1}`} aria-label={`Starter question ${i + 1}`} value={q}
                 style={{ ...inputStyle, marginBottom: '6px' }}
                 onChange={(e) => set({ starter_questions: bot.starter_questions.map((x, j) => (j === i ? e.target.value : x)) })} />
        ))}
      </Field>
      <Field id={`${detail.site_id}-contact`} label="How to reach the organization">
        <input id={`${detail.site_id}-contact`} value={bot.contact_text} style={inputStyle}
               onChange={(e) => set({ contact_text: e.target.value })} />
      </Field>
      <Field id={`${detail.site_id}-refusal`} label="When the answer isn't on the sites"
             help="{agency} and {contact} are filled in. Leave empty for the built-in reply.">
        <textarea id={`${detail.site_id}-refusal`} rows={3} value={bot.refusal_message} style={inputStyle}
                  onChange={(e) => set({ refusal_message: e.target.value })} />
      </Field>
      <p style={hint}>Branding, colors and the rest of the widget are in the tenant's own dashboard (Widget &amp; Persona).</p>
      <ErrorList error={error} />
      {saved && <p role="status" style={{ ...hint, color: '#065f46' }}>Saved. The widget picks it up within a minute.</p>}
      <button type="button" style={btnPrimary} disabled={busy} onClick={save}>Save</button>
    </div>
  );
}

// The web addresses whose pages may load this tenant's widget. A page missing
// here gets 403s on branding and on every question: the widget shows default
// branding and "This assistant isn't enabled on this website yet."
function EmbeddingTab({ api, detail, onSaved }) {
  const [text, setText] = React.useState((detail.allowed_origins || []).join('\n'));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saved, setSaved] = React.useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    const origins = text.split('\n').map((s) => s.trim()).filter(Boolean);
    try {
      const { ok, data } = await api(`/api/admin/sites/${encodeURIComponent(detail.site_id)}`, {
        method: 'PUT', body: JSON.stringify({ allowed_origins: origins }),
      });
      if (!ok) setError(apiErrors(data, 'save failed'));
      else {
        setSaved(true);
        if (onSaved) onSaved();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Field id={`${detail.site_id}-origins`} label="Websites that may show this assistant"
             help={'One per line, as the address bar starts: https://www.example.org (no path). '
                   + '*.example.org allows every subdomain. www and non-www are different addresses.'}>
        <textarea id={`${detail.site_id}-origins`} rows={5} value={text} style={{ ...inputStyle, fontFamily: 'monospace' }}
                  onChange={(e) => { setText(e.target.value); setSaved(false); }} />
      </Field>
      {detail.is_demo && (
        <p style={hint}>Keep our own address on the list, or the demo page stops working.</p>
      )}
      <ErrorList error={error} />
      {saved && <p role="status" style={{ ...hint, color: '#065f46' }}>Saved. Embeds on these sites work within a minute.</p>}
      <button type="button" style={btnPrimary} disabled={busy} onClick={save}>Save</button>
    </div>
  );
}

// The organization's own words for what visitors call something else
// (app/services/local_terms.py): residents ask about the "city council", the
// town's pages say "Board of Commissioners". A question using a listed word is
// searched with the organization's word as well.
function TermsTab({ api, detail, onSaved }) {
  const [text, setText] = React.useState(termsText(detail.local_terms));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saved, setSaved] = React.useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { ok, data } = await api('/api/corpus/local-terms', {
        method: 'PUT', body: JSON.stringify({ corpus_id: detail.channel_url, local_terms: parseTerms(text) }),
      });
      if (!ok) setError(apiErrors(data, 'save failed'));
      else {
        setText(termsText(data.local_terms));
        setSaved(true);
        if (onSaved) onSaved(data.local_terms);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Field id={`${detail.site_id}-terms`} label="Words people use for the organization's own terms"
             help={'One per line: the word the sites use, then "=", then what people call it, comma-separated. '
                   + 'Board of Commissioners = city council, town council'}>
        <textarea id={`${detail.site_id}-terms`} rows={8} value={text} style={{ ...inputStyle, fontFamily: 'monospace' }}
                  placeholder="Town Hall = city hall"
                  onChange={(e) => { setText(e.target.value); setSaved(false); }} />
      </Field>
      <p style={hint}>Shared by every tenant on this corpus. Takes effect within a minute; no recrawl needed.</p>
      <ErrorList error={error} />
      {saved && <p role="status" style={{ ...hint, color: '#065f46' }}>Saved.</p>}
      <button type="button" style={btnPrimary} disabled={busy} onClick={save}>Save</button>
    </div>
  );
}

// Facts the organization approved that none of its pages state
// (app/services/staff_answers.py): "How do I make a noise complaint?" has no
// answer on Mooresville's site. Each one is searched like a page and cited as
// "<organization> staff answer: <first question>", linked to its page.
const BLANK_STAFF = { id: '', questionsText: '', answer: '', link: '', approvedOn: '' };

function StaffTab({ api, detail, onSaved }) {
  const [rows, setRows] = React.useState(staffRows(detail.staff_answers));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saved, setSaved] = React.useState(false);
  const set = (i, patch) => { setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))); setSaved(false); };
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { ok, data } = await api('/api/corpus/staff-answers', {
        method: 'PUT', body: JSON.stringify({ corpus_id: detail.channel_url, staff_answers: staffPayload(rows) }),
      });
      if (!ok) setError(apiErrors(data, 'save failed'));
      else {
        setRows(staffRows(data.staff_answers));
        setSaved(true);
        if (onSaved) onSaved(data.staff_answers);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p style={hint}>
        Only answers the organization has approved. Each is searched like a page and cited as a staff answer,
        linked to the page you give. Shared by every tenant on this corpus; takes effect within a minute.
      </p>
      {rows.map((r, i) => {
        const id = `staff-${i + 1}`;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={{ padding: '10px 12px', border: `1px solid ${BRAND.rule}`, borderRadius: '8px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input aria-label={`${id} short name`} value={r.id} placeholder="noise-complaints"
                     onChange={(e) => set(i, { id: e.target.value })} style={{ ...inputStyle, flex: '1 1 160px' }} />
              <input aria-label={`${id} approved on`} type="date" value={r.approvedOn}
                     title="When the organization approved this answer (shown as its date)"
                     onChange={(e) => set(i, { approvedOn: e.target.value })} style={{ ...inputStyle, width: 'auto' }} />
              <button type="button" style={btnSmall} aria-label={`${id} remove`}
                      onClick={() => { setRows(rows.filter((_, j) => j !== i)); setSaved(false); }}>Remove</button>
            </div>
            <textarea aria-label={`${id} questions`} rows={3} value={r.questionsText}
                      placeholder={'How do I make a noise complaint?\nWhat should I do if my neighbors are too loud?'}
                      onChange={(e) => set(i, { questionsText: e.target.value })}
                      style={{ ...inputStyle, marginTop: '6px' }} />
            <textarea aria-label={`${id} answer`} rows={3} value={r.answer}
                      placeholder="Call the Police Department's non-emergency line at 704-664-3311."
                      onChange={(e) => set(i, { answer: e.target.value })} style={{ ...inputStyle, marginTop: '6px' }} />
            <input aria-label={`${id} link`} value={r.link} placeholder="https://www.example.gov/police"
                   onChange={(e) => set(i, { link: e.target.value })} style={{ ...inputStyle, marginTop: '6px' }} />
          </div>
        );
      })}
      <p style={hint}>Questions: one per line, the first is the title. Answer: plain text, no web addresses (the link carries it).</p>
      <ErrorList error={error} />
      {saved && <p role="status" style={{ ...hint, color: '#065f46' }}>Saved.</p>}
      <button type="button" style={btnSmall} onClick={() => { setRows([...rows, { ...BLANK_STAFF }]); setSaved(false); }}>
        + Answer
      </button>{' '}
      <button type="button" style={btnPrimary} disabled={busy} onClick={save}>Save</button>
    </div>
  );
}

// Unset is not a kind: the server falls back to pension. The select starts
// empty then, so saving "pension" is a choice (SDCERS), not an accident.
function DomainTab({ api, detail, onSaved }) {
  const [domain, setDomain] = React.useState(detail.domain_set === false ? '' : (detail.domain || 'pension'));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saved, setSaved] = React.useState(false);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { ok, data } = await api('/api/corpus/domain', {
        method: 'PUT', body: JSON.stringify({ corpus_id: detail.channel_url, domain }),
      });
      if (!ok) setError(apiErrors(data, 'save failed'));
      else {
        setSaved(true);
        if (onSaved) onSaved(domain);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Field id={`${detail.site_id}-domain`} label="Kind of organization"
             help={[DOMAINS.find((d) => d.id === domain)?.help,
                    'Decides which guards run and how prompts are worded. Shared by every tenant on this corpus.']
               .filter(Boolean).join(' ')}>
        <select id={`${detail.site_id}-domain`} value={domain} style={inputStyle}
                onChange={(e) => { setDomain(e.target.value); setSaved(false); }}>
          <option value="" disabled>Not set (answering as a pension system)</option>
          {DOMAINS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </Field>
      <ErrorList error={error} />
      {saved && <p role="status" style={{ ...hint, color: '#065f46' }}>Saved.</p>}
      <button type="button" style={btnPrimary} disabled={busy || !domain} onClick={save}>Save</button>
    </div>
  );
}

export default function TenantSettings({ api, siteId, onClose, onRecrawl, onSaved }) {
  const [detail, setDetail] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [tab, setTab] = React.useState('sites');
  const [sitesVersion, setSitesVersion] = React.useState(0);
  const [sitesNote, setSitesNote] = React.useState('');

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const { ok, data } = await api(`/api/admin/ops/tenant/${encodeURIComponent(siteId)}`);
      if (!ok) throw new Error(apiErrors(data, 'could not load the tenant'));
      setDetail(data);
    } catch (e) {
      setError(e.message);
    }
  }, [api, siteId]);

  React.useEffect(() => { load(); }, [load]);

  const tabs = [
    { id: 'sites', label: 'Sites & crawl' },
    { id: 'assistant', label: 'Assistant' },
    { id: 'embedding', label: 'Embedding' },
    { id: 'domain', label: 'Kind' },
    { id: 'terms', label: 'Local terms' },
    { id: 'staff', label: 'Staff answers' },
    // Also shown while trip planning is on: a kind change doesn't turn it off,
    // and this tab is the only place an operator can.
    ...(detail && (detail.domain === 'transit' || (detail.transit && detail.transit.enabled))
      ? [{ id: 'transit', label: 'Transit' }] : []),
  ];

  return (
    <div style={{ ...card, padding: '16px 18px', margin: '8px 0 14px', background: '#fcfcfd' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <b style={{ fontSize: '0.9rem' }}>Settings · {siteId}</b>
        <span style={{ fontSize: '0.78rem', color: BRAND.gray500 }}>{detail ? detail.channel_url : ''}</span>
        <span style={{ flex: 1 }} />
        <button type="button" style={btnSmall} onClick={onClose}>Close</button>
      </div>
      {error && <ErrorList error={error} />}
      {!detail && !error && <p style={hint}>Loading…</p>}
      {detail && (
        <>
          <div role="tablist" style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${BRAND.rule}`, marginBottom: '14px' }}>
            {tabs.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
                      style={{ ...btnSmall, marginRight: 0, border: 'none', borderRadius: 0,
                               borderBottom: tab === t.id ? `2px solid ${BRAND.ink900}` : '2px solid transparent',
                               color: tab === t.id ? BRAND.ink900 : BRAND.gray500, fontWeight: tab === t.id ? 600 : 500 }}>
                {t.label}
              </button>
            ))}
          </div>
          {detail.settings_error && <ErrorList error={detail.settings_error} />}
          {detail.domain_set === false && (
            <div style={{ ...warnBox, margin: '0 0 12px' }}>
              <b>Domain not set — answering as a pension system.</b> Every question gets the pension advice guard
              (“I can't calculate your individual benefit…”), the look-alike-agency guard and “member” wording.
              {tab !== 'domain' && (
                <>{' '}<button type="button" style={btnSmall} onClick={() => setTab('domain')}>Choose the kind</button></>
              )}
            </div>
          )}
          {tab === 'sites' && (
            <SitesTab key={sitesVersion} api={api} detail={detail} onRecrawl={onRecrawl} note={sitesNote}
                      onStored={(crawl, note) => {
                        setDetail((d) => ({ ...d, crawl }));
                        setSitesNote(note);
                        setSitesVersion((v) => v + 1);
                        if (onSaved) onSaved();
                      }} />
          )}
          {tab === 'assistant' && <AssistantTab api={api} detail={detail} onSaved={load} />}
          {tab === 'embedding' && <EmbeddingTab api={api} detail={detail} onSaved={() => { load(); if (onSaved) onSaved(); }} />}
          {tab === 'domain' && (
            <DomainTab api={api} detail={detail}
                       onSaved={(domain) => { setDetail({ ...detail, domain, domain_set: true }); if (onSaved) onSaved(); }} />
          )}
          {tab === 'terms' && (
            <TermsTab api={api} detail={detail}
                      onSaved={(terms) => { setDetail({ ...detail, local_terms: terms }); if (onSaved) onSaved(); }} />
          )}
          {tab === 'staff' && (
            <StaffTab api={api} detail={detail}
                      onSaved={(answers) => { setDetail({ ...detail, staff_answers: answers }); if (onSaved) onSaved(); }} />
          )}
          {tab === 'transit' && detail.domain !== 'transit' && (
            <div style={{ ...warnBox, margin: '0 0 12px' }}>
              <b>Trip planning is on, but this tenant's kind isn't Transit.</b> Set the kind back to Transit, or turn
              trip planning off below.
            </div>
          )}
          {tab === 'transit' && <TransitPanel api={api} corpusId={detail.channel_url} transit={detail.transit} />}
        </>
      )}
    </div>
  );
}
